import React, {cloneElement, useEffect, useMemo, useState} from "react";
import Draggable from "react-draggable";
import PopupState, {bindPopper, bindToggle} from "material-ui-popup-state";
import {Box, Button, Paper, Popper, useTheme, Zoom} from "@mui/material";
import {VisibilityRounded} from "@mui/icons-material";
import classnames from "classnames";
import UIUtils from "../../../utils";


/**
 * @type {React.FC<React.PropsWithChildren<{
 *     wrapperProps?: Partial<import('@mui/material').BoxProps>,
 *     buttonProps?: Partial<import('@mui/material').ButtonProps>,
 *     iconProps?: Partial<import('@mui/material').SvgIconProps>,
 *     popoverElement: React.ReactNode,
 *     popupId: string,
 *     width: number,
 *     height: number,
 *     canRenderPopover: boolean,
 *     popperProps?: Partial<import('@mui/material').PopperProps>,
 * }>>}
 */
const DraggablePopover = ({
                              children,
                              canRenderPopover,
                              popoverElement,
                              popperProps,
                              wrapperProps,
                              buttonProps,
                              iconProps,
                              popupId,
                              width = 700,
                              height = 500,
                              iconOnRight = true,
                              draggableProps = {},
                          }) => {
    const [popperRef, setPopperRef] = useState(null)
    const theme = useTheme();

    const childrenValid = useMemo(() => children && React.Children.count(children) === 1, [children]);

    /**
     * Shows the warning if the children are not valid.
     */
    useEffect(() => {
        if (!childrenValid) {
            console.warn('DraggablePopover expects exactly one child');
        }
    }, [childrenValid, canRenderPopover])

    /**
     * Shows the warning if the partNo and partId are not provided
     */
    useEffect(() => {
        if (!popoverElement) {
            console.warn('DraggablePopover requires popoverElement to be provided');
        }
    }, [popoverElement])


    return (
        <>
            {
                (!canRenderPopover || !childrenValid) &&
                children
            }
            {
                canRenderPopover && childrenValid &&
                <PopupState variant={"popper"} popupId={popupId}>
                    {(popupState) => {
                        let defaultPosition = {x: 16, y: 16,};
                        if (popupState.anchorEl) {
                            const rect = popupState.anchorEl.getBoundingClientRect();
                            const widthOfBox = Math.min(window.innerWidth * 0.9, width);
                            const heightOfBox = Math.min(window.innerHeight * 0.9, height);
                            defaultPosition = {
                                x: Math.max(Math.min(rect.x - (widthOfBox / 2)), 0),
                                y: Math.max(Math.min(rect.y - (heightOfBox / 2)), 0),
                            };
                        }
                        // WITHOUT THAT LINE, DRAGGING IS NOT WORKING, BUT THERE'S NO ERROR
                        popupState.anchorEl = null;
                        const toggleState = bindToggle(popupState);
                        const newToggleState = {
                            ...toggleState,
                        }
                        newToggleState.onClick = (e) => {
                            if (wrapperProps?.onClick) {
                                wrapperProps.onClick(e);
                            }
                            toggleState.onClick(e);
                        }
                        newToggleState.onTouchStart = (e) => {
                            if (wrapperProps?.onTouchStart) {
                                wrapperProps.onTouchStart(e);
                            }
                            toggleState.onTouchStart(e);
                        }
                        return (
                            <>
                                <Box
                                    component={'span'}
                                    {...wrapperProps ?? {}}
                                    {...newToggleState}
                                    sx={(theme) => ({
                                        ...UIUtils.invokeSx(wrapperProps?.sx, theme),
                                        [`&:hover:not(.Mui-disabled)`]: {
                                            cursor: 'pointer !important',
                                            "*": {
                                                cursor: 'pointer !important',
                                            },
                                        },
                                    })}
                                >
                                    <Button
                                        variant={'text'}
                                        color={'primary'}
                                        {...buttonProps}
                                        className={classnames('d-flex justify-content-center part-number-fluid', buttonProps?.className)}
                                    >
                                        {
                                            !iconOnRight &&
                                            <VisibilityRounded
                                                {...iconProps}
                                                sx={{
                                                    marginTop: 0.4,
                                                    ...UIUtils.invokeSx(iconProps?.sx, theme),
                                                }}
                                                className={classnames('icon small mr-1', iconProps?.className)}
                                            />
                                        }

                                        {children}
                                        {
                                            iconOnRight &&
                                            <VisibilityRounded
                                                {...iconProps}
                                                sx={{
                                                    marginTop: 0.4,
                                                    ...UIUtils.invokeSx(iconProps?.sx, theme),
                                                }}
                                                className={classnames('icon small ml-1', iconProps?.className)}
                                            />
                                        }
                                    </Button>
                                </Box>
                                {
                                    popupState.isOpen && // can not remove this otherwise all popovers will open at the same place.
                                    <Draggable defaultPosition={defaultPosition} bounds={'#root'} {...draggableProps}>
                                        <Popper
                                            {...bindPopper(popupState)}
                                            popperRef={(popperRef) => setPopperRef(popperRef)}
                                            transition
                                            placement="top"
                                            {...(popperProps ?? {})}
                                            slotProps={{
                                                ...(popperProps?.slotProps ?? {}),
                                                root: {
                                                    ...(popperProps?.slotProps?.root ?? {}),
                                                    style: {
                                                        ...(popperProps?.slotProps?.root?.style ?? {}),
                                                        zIndex: theme.zIndex.modal + 1,
                                                    }
                                                },
                                            }}
                                        >
                                            <Zoom in={popupState.isOpen} unmountOnExit mountOnEnter>
                                                <Paper sx={{position: 'relative', boxShadow: theme.shadows[6], borderRadius: 1, overflow: 'hidden'}}>
                                                    {
                                                        cloneElement(popoverElement, {
                                                            ...popoverElement.props,
                                                            popperInstance: popperRef,
                                                            close: popupState.close,
                                                            width: width,
                                                            height: height,
                                                        })
                                                    }
                                                </Paper>
                                            </Zoom>
                                        </Popper>
                                    </Draggable>
                                }
                            </>
                        );
                    }}
                </PopupState>
            }
        </>
    );
}

export default DraggablePopover;